import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import axiosInstance from 'helpers/api';

interface EmailAnalytics {
  emailsProcessed: number;
  leadsIdentified: number;
  responseRate: number;
  avgResponseTime: string;
  recentActivity: number;
  sentEmails: number;
  receivedEmails: number;
  aiAnalysisRate: number;
  analyzedEmails: number;
  insights: {
    totalThreads: number;
    threadsWithResponses: number;
    engagementTrend: 'increasing' | 'stable' | 'decreasing';
    topPerformingTimes: string[];
  };
}

interface UseEmailAnalyticsReturn {
  analytics: EmailAnalytics | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useEmailAnalytics = (emailAddress: string): UseEmailAnalyticsReturn => {
  const [analytics, setAnalytics] = useState<EmailAnalytics | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = async () => {
    if (!emailAddress) {
      setError('Email address is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log(`📊 [EMAIL ANALYTICS HOOK] Fetching analytics for: ${emailAddress}`);

      const response = await axiosInstance.get(`/emails/analytics/${encodeURIComponent(emailAddress)}`);
      
      if (response.data.success && response.data.data) {
        console.log('✅ [EMAIL ANALYTICS HOOK] Analytics fetched successfully:', response.data.data);
        setAnalytics(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to fetch email analytics');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch email analytics';
      console.error('❌ [EMAIL ANALYTICS HOOK] Error:', errorMessage);
      setError(errorMessage);
      
      // Show user-friendly error message
      toast.error('Unable to load email analytics. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (emailAddress) {
      fetchAnalytics();
    }
  }, [emailAddress]);

  const refetch = () => {
    fetchAnalytics();
  };

  return {
    analytics,
    loading,
    error,
    refetch,
  };
}; 